import React, { useContext, useMemo } from 'react';

/**
 * We use this to build unique ids. Consult `useUniqueId` for more information.
 *
 * Ids are generated using this template string: `${UniqueIdSource.prefix}-${UniqueIdSource.index}`.
 */
interface UniqueIdSource {
  /**
   * Used to differentiate unique id sources.
   */
  prefix: string;

  /**
   * The counter that keeps track of how many unique ids we've generated from this source.
   */
  index: number;
}

/** Creates a `UniqueIdSource` for use with our `UniqueIdContext` */
export function makeUniqueIdSource(prefix: string): UniqueIdSource {
  return {
    prefix,
    index: 0,
  };
}

/** Stores a `UniqueIdSource`. The provider for this should be placed in the root app component before you try using `useUniqueId` */
export const UniqueIdContext = React.createContext<UniqueIdSource>(
  makeUniqueIdSource('global')
);

/**
 * Pulls the `UniqueIdContext` and returns a unique id while incrementing the context's index.
 *
 * This is useful for situations where we need to link components via `aria-owns` or similar.
 */
export function useUniqueId() {
  const idSource = useContext(UniqueIdContext);

  const value = useMemo(() => {
    const id = `${idSource.prefix}-${idSource.index}`;

    // Increment the idSource's index so the next component that uses this will have an index one higher
    idSource.index++;

    return id;
  }, [idSource]);

  return value;
}
